import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileSpreadsheet, Upload, Trash2, ArrowLeft, Search, ArrowUpDown, Database } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const COLORS = ["#aa0000", "#7284c4", "#e0a458", "#3f7d5a", "#c05780", "#2f3e6e"];
const fmtNum = (n) => new Intl.NumberFormat("id-ID").format(n);

export default function ExcelDashboardPanel() {
  const [datasets, setDatasets] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadList = useCallback(() => api.get("/excel/datasets").then((r) => setDatasets(r.data)).catch(() => {}), []);
  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = async (id) => {
    try { const r = await api.get(`/excel/datasets/${id}`); setDetail(r.data); }
    catch { toast.error("Gagal membuka dataset"); }
  };

  const upload = async (f) => {
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/excel/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Dashboard otomatis dibuat dari "${res.data.name}"`);
      await loadList();
      openDetail(res.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal upload Excel");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus dataset ini?")) return;
    await api.delete(`/excel/datasets/${id}`);
    toast.success("Dataset dihapus");
    loadList();
  };

  if (detail) return <DatasetDetail data={detail} onBack={() => { setDetail(null); loadList(); }} />;

  return (
    <div data-testid="excel-dashboard-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><FileSpreadsheet className="w-6 h-6 text-showty-red" /> Dashboard Excel</h3>
          <p className="text-sm text-muted-foreground">Upload file Excel (.xlsx) — sistem otomatis membuat dashboard dari isinya.</p>
        </div>
        <label className="inline-flex items-center gap-2 rounded-full bg-showty-red text-white px-5 py-2.5 text-sm font-medium cursor-pointer hover:bg-showty-red/90" data-testid="excel-upload-label">
          <Upload className="w-4 h-4" /> {loading ? "Memproses..." : "Upload Excel"}
          <input type="file" accept=".xlsx,.xls" className="hidden" disabled={loading} onChange={(e) => upload(e.target.files?.[0])} data-testid="excel-upload-input" />
        </label>
      </div>

      {datasets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-20 text-center" data-testid="excel-empty">
          <Database className="w-8 h-8 mx-auto mb-3 text-showty-red/40" />
          <p className="text-sm text-muted-foreground">Belum ada dataset. Download sheet kamu sebagai .xlsx lalu upload di sini.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="excel-dataset-list">
          {datasets.map((d) => (
            <div key={d.id} className="rounded-2xl border border-border bg-white p-5 hover:shadow-md transition-shadow" data-testid={`excel-ds-${d.id}`}>
              <div className="flex items-start justify-between">
                <FileSpreadsheet className="w-8 h-8 text-showty-red" />
                <button onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive" data-testid={`excel-ds-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <h4 className="mt-3 font-medium text-showty-navy truncate" title={d.name}>{d.name}</h4>
              <p className="text-xs text-muted-foreground mt-1">{d.sheet_count} sheet · {fmtNum(d.total_rows)} baris</p>
              <p className="text-[11px] text-muted-foreground">{(d.created_at || "").slice(0, 10)}</p>
              <Button onClick={() => openDetail(d.id)} className="mt-4 w-full rounded-full" data-testid={`excel-ds-open-${d.id}`}>Buka Dashboard</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DatasetDetail = ({ data, onBack }) => (
  <div data-testid="excel-detail">
    <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-showty-navy mb-4 hover:underline" data-testid="excel-back"><ArrowLeft className="w-4 h-4" /> Kembali ke daftar</button>
    <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><FileSpreadsheet className="w-6 h-6 text-showty-red" /> {data.name}</h3>
    <Tabs defaultValue={data.sheets[0]?.name} className="mt-4">
      <TabsList className="bg-showty-secondary flex-wrap h-auto">
        {data.sheets.map((s) => <TabsTrigger key={s.name} value={s.name} data-testid={`excel-sheet-tab-${s.name}`}>{s.name}</TabsTrigger>)}
      </TabsList>
      {data.sheets.map((s) => <TabsContent key={s.name} value={s.name}><SheetView sheet={s} /></TabsContent>)}
    </Tabs>
  </div>
);

const SheetView = ({ sheet }) => {
  const [q, setQ] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [limit, setLimit] = useState(50);

  const numeric = Object.entries(sheet.numeric_summary || {});
  const categories = Object.entries(sheet.category_summary || {});

  const rows = useMemo(() => {
    let r = sheet.rows;
    if (q.trim()) {
      const s = q.toLowerCase();
      r = r.filter((row) => sheet.columns.some((c) => String(row[c] ?? "").toLowerCase().includes(s)));
    }
    if (sortCol) {
      r = [...r].sort((a, b) => {
        const av = a[sortCol], bv = b[sortCol];
        const an = parseFloat(String(av).replace(/[^0-9.-]/g, ""));
        const bn = parseFloat(String(bv).replace(/[^0-9.-]/g, ""));
        const cmp = (!isNaN(an) && !isNaN(bn)) ? an - bn : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return r;
  }, [sheet, q, sortCol, sortDir]);

  const toggleSort = (c) => {
    if (sortCol === c) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortCol(c); setSortDir("asc"); }
  };

  return (
    <div className="space-y-6 mt-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-border bg-white p-4" data-testid="excel-summary-count">
          <p className="text-xs font-medium text-showty-navy">Jumlah Baris</p>
          <p className="font-display text-3xl text-showty-navy mt-1">{fmtNum(sheet.row_count)}</p>
          <p className="text-[10px] text-muted-foreground">COUNT</p>
        </div>
        {numeric.map(([col, st]) => (
          <div key={col} className="rounded-2xl border border-border bg-white p-4" data-testid={`excel-num-${col}`}>
            <p className="text-xs font-medium text-showty-navy truncate" title={col}>{col}</p>
            <p className="font-display text-2xl text-showty-red mt-1">{fmtNum(st.sum)}</p>
            <p className="text-[10px] text-muted-foreground">TOTAL (SUM)</p>
            <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] text-muted-foreground text-center">
              <span>AVG<br /><b className="text-showty-navy">{fmtNum(st.avg)}</b></span>
              <span>MIN<br /><b className="text-showty-navy">{fmtNum(st.min)}</b></span>
              <span>MAX<br /><b className="text-showty-navy">{fmtNum(st.max)}</b></span>
            </div>
          </div>
        ))}
      </div>

      {categories.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {categories.map(([col, items]) => (
            <div key={col} className="rounded-2xl border border-border bg-white p-4" data-testid={`excel-chart-${col}`}>
              <p className="text-sm font-medium text-showty-navy mb-2">{col} <span className="text-[10px] text-muted-foreground">(COUNT per kategori)</span></p>
              <ResponsiveContainer width="100%" height={Math.max(160, items.length * 28)}>
                <BarChart data={items} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="value" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {items.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="relative max-w-xs mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setLimit(50); }} placeholder="Cari di tabel..." className="pl-9 rounded-full" data-testid="excel-table-search" />
        </div>
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-sm" data-testid="excel-table">
            <thead className="bg-showty-cream/60">
              <tr>{sheet.columns.map((c) => (
                <th key={c} onClick={() => toggleSort(c)} className="text-left px-3 py-2.5 font-semibold text-showty-navy whitespace-nowrap cursor-pointer select-none hover:bg-showty-pink/20">
                  <span className="inline-flex items-center gap-1">{c}<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map((row, ri) => (
                <tr key={ri} className="border-t border-border hover:bg-showty-pink/10">
                  {sheet.columns.map((c) => <td key={c} className="px-3 py-2 text-showty-navy whitespace-nowrap">{String(row[c] ?? "")}</td>)}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={sheet.columns.length} className="px-3 py-8 text-center text-muted-foreground">Tidak ada data cocok.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span>Menampilkan {Math.min(limit, rows.length)} dari {fmtNum(rows.length)} baris</span>
          {limit < rows.length && <Button variant="outline" size="sm" className="rounded-full" onClick={() => setLimit(limit + 100)} data-testid="excel-load-more">Tampilkan lebih</Button>}
        </div>
      </div>
    </div>
  );
};
